import type { EventNode } from "../types";

import {
  SPAN_BEGIN,
  STATE,
  STORE,
  TOOL,
  TYPE_AGENT,
  TYPE_CHECKPOINT,
  TYPE_HANDOFF,
  TYPE_SOLVER,
  TYPE_SOLVERS,
  TYPE_SUBTASK,
  TYPE_TOOL,
} from "./utils";

export const transformTree = (roots: EventNode[]): EventNode[] => {
  // Gather the transformers that we'll use
  const treeNodeTransformers: TreeNodeTransformer[] = transformers();

  const visitNode = (node: EventNode): EventNode | EventNode[] => {
    // Start with the original node
    let currentNodes: EventNode[] = [node];

    // Process children of all nodes first (depth-first)
    currentNodes = currentNodes.map((n) => {
      n.children = n.children.flatMap(visitNode);
      return n;
    });

    // Apply each transformer to all nodes that match
    for (const transformer of treeNodeTransformers) {
      const nextNodes: EventNode[] = [];

      // Process each current node with this transformer
      for (const currentNode of currentNodes) {
        if (transformer.matches(currentNode)) {
          const result = transformer.process(currentNode);
          if (Array.isArray(result)) {
            nextNodes.push(...result);
          } else {
            nextNodes.push(result);
          }
        } else {
          // Node doesn't match this transformer, keep it unchanged
          nextNodes.push(currentNode);
        }
      }

      // Update current nodes for next transformer
      currentNodes = nextNodes;
    }

    // Return all processed nodes
    const only = currentNodes.length === 1 ? currentNodes[0] : undefined;
    return only ?? currentNodes;
  };

  // Process all nodes first
  const processedRoots = roots.flatMap(visitNode);

  // Call flush on any transformers that have it
  const flushedNodes: EventNode[] = [];
  for (const transformer of treeNodeTransformers) {
    if (transformer.flush) {
      const flushResults = transformer.flush();
      if (flushResults.length > 0) {
        flushedNodes.push(...flushResults);
      }
    }
  }

  return [...processedRoots, ...flushedNodes];
};

const transformers = () => {
  const treeNodeTransformers: TreeNodeTransformer[] = [
    {
      name: "unwrap_main",
      matches: (node) =>
        node.event.event === SPAN_BEGIN && node.event.type === "main",
      process: unwrapNode,
    },
    {
      name: "unwrap_tools",
      matches: (node) =>
        node.event.event === SPAN_BEGIN && node.event.type === TYPE_TOOL,
      process: (node) => elevateChildNode(node, TYPE_TOOL) || node,
    },
    {
      name: "unwrap_subtasks",
      matches: (node) =>
        node.event.event === SPAN_BEGIN && node.event.type === TYPE_SUBTASK,
      process: (node) => elevateChildNode(node, TYPE_SUBTASK) || node,
    },
    {
      name: "unwrap_agent_solver",
      matches: (node) =>
        node.event.event === SPAN_BEGIN &&
        node.event["type"] === TYPE_SOLVER &&
        node.children.length === 2 &&
        node.children[0]?.event.event === SPAN_BEGIN &&
        node.children[0].event.type === TYPE_AGENT &&
        node.children[1]?.event.event === STATE,

      process: (node) => skipFirstChildNode(node),
    },
    {
      name: "unwrap_agent_solver w/store",
      matches: (node) =>
        node.event.event === SPAN_BEGIN &&
        node.event["type"] === TYPE_SOLVER &&
        node.children.length === 3 &&
        node.children[0]?.event.event === SPAN_BEGIN &&
        node.children[0].event.type === TYPE_AGENT &&
        node.children[1]?.event.event === STATE &&
        node.children[2]?.event.event === STORE,
      process: (node) => skipFirstChildNode(node),
    },
    {
      name: "unwrap_handoff",
      matches: (node) => {
        const isHandoffNode =
          node.event.event === SPAN_BEGIN &&
          node.event["type"] === TYPE_HANDOFF;

        if (!isHandoffNode) {
          return false;
        }

        if (node.children.length === 1) {
          return (
            node.children[0]?.event.event === TOOL &&
            !!node.children[0].event.agent
          );
        } else {
          return (
            node.children.length === 2 &&
            node.children[0]?.event.event === TOOL &&
            node.children[1]?.event.event === STORE &&
            node.children[0].children.length === 2 &&
            node.children[0].children[0]?.event.event === SPAN_BEGIN &&
            node.children[0].children[0].event.type === TYPE_AGENT
          );
        }
      },
      process: (node) => skipThisNode(node),
    },
    {
      name: "collapse_same_name_spans",
      matches: (node) =>
        isAgentOrSolverSpan(node) &&
        node.children.some((child) => isSameNameSpanChild(node, child)),
      process: (node) => collapseSameNameSpans(node),
    },
    {
      name: "discard_solvers_span",
      matches: (Node) =>
        Node.event.event === SPAN_BEGIN && Node.event.type === TYPE_SOLVERS,
      process: (node) => {
        const nodes = discardNode(node);
        return nodes;
      },
    },
    {
      name: "discard_checkpoint_span",
      matches: (node) =>
        node.event.event === SPAN_BEGIN && node.event.type === TYPE_CHECKPOINT,
      process: (node) => discardNode(node),
    },
  ];
  return treeNodeTransformers;
};

type TreeNodeTransformer = {
  name: string;
  matches: (node: EventNode) => boolean;
  process: (node: EventNode) => EventNode | EventNode[];
  flush?: () => EventNode[];
};

/**
 * Process a span node by elevating a specific child node type and moving its siblings as children
 * @template T - Type of the event (either ToolEvent or SubtaskEvent)
 */
const elevateChildNode = (
  node: EventNode,
  childEventType: "tool" | "subtask"
): EventNode | null => {
  // Find the specific event child
  const targetIndex = node.children.findIndex(
    (child) => child.event.event === childEventType
  );

  if (targetIndex === -1) {
    // Tool/subtask events have span_id set to the parent span (not the
    // tool span itself), so they end up as siblings rather than children.
    // When this happens, return null to keep the span node as-is.
    return null;
  }

  const target = node.children[targetIndex];
  if (!target) return null;

  // Get the target node and set its depth
  const targetNode = { ...target };
  const remainingChildren = node.children.filter((_, i) => i !== targetIndex);

  // Process the remaining children
  targetNode.depth = node.depth;
  targetNode.children = setDepth(remainingChildren, node.depth + 1);

  // No need to update the event itself (events have been deprecated
  // and more importantly we drive children / transcripts using the tree structure itself
  // and notes rather than the event.events itself)
  return targetNode;
};

const isAgentOrSolverSpan = (node: EventNode): boolean =>
  node.event.event === SPAN_BEGIN &&
  (node.event.type === TYPE_SOLVER || node.event.type === TYPE_AGENT);

// Strip the package prefix so a solver span (named via registry_log_name, which
// only strips the inspect_ai/ prefix, e.g. "inspect_swe/codex_cli") matches its
// inner agent span (named via the unqualified registry name, e.g. "codex_cli").
const unqualifiedSpanName = (name: string): string => {
  const slash = name.indexOf("/");
  return slash === -1 ? name : name.slice(slash + 1);
};

const isSameNameSpanChild = (parent: EventNode, child: EventNode): boolean =>
  isAgentOrSolverSpan(child) &&
  parent.event.event === SPAN_BEGIN &&
  child.event.event === SPAN_BEGIN &&
  unqualifiedSpanName(parent.event.name) ===
    unqualifiedSpanName(child.event.name);

// Collapse a same-named agent/solver child into its parent by hoisting the
// child's children up one level (e.g. a react_agent span nested directly
// inside another react_agent span). Runs depth-first, so chains of any length
// flatten into the outermost span.
const collapseSameNameSpans = (node: EventNode): EventNode => {
  node.children = node.children.flatMap((child) =>
    isSameNameSpanChild(node, child) ? reduceDepth(child.children, 1) : [child]
  );
  return node;
};

const unwrapNode = (node: EventNode): EventNode[] => {
  return node.children.map((child) => {
    child.depth = node.depth;
    return child;
  });
};

const skipFirstChildNode = (node: EventNode): EventNode => {
  const agentSpan = node.children.splice(0, 1)[0];
  node.children.unshift(...reduceDepth(agentSpan?.children || []));
  return node;
};

const skipThisNode = (node: EventNode): EventNode => {
  const first = node.children[0];
  if (!first) return node;
  const newNode = { ...first };
  newNode.depth = node.depth;
  newNode.children = reduceDepth(newNode.children, 2);
  return newNode;
};

const discardNode = (node: EventNode): EventNode[] => {
  const nodes = reduceDepth(node.children, 1);
  return nodes;
};

// Reduce the depth of the children by 1
// This is used when we hoist a child node to the parent
const reduceDepth = (nodes: EventNode[], depth: number = 1): EventNode[] => {
  return nodes.map((node) => {
    if (node.children.length > 0) {
      node.children = reduceDepth(node.children, 1);
    }
    node.depth = node.depth - depth;
    return node;
  });
};

const setDepth = (nodes: EventNode[], depth: number): EventNode[] => {
  return nodes.map((node) => {
    if (node.children.length > 0) {
      node.children = setDepth(node.children, depth + 1);
    }
    node.depth = depth;
    return node;
  });
};
